/* ============================================
   AudioPlayer — Custom audio player for messages
   Play/pause, seekable progress, speed selector
   Static waveform visualization
   ============================================ */
import { useState, useRef, useEffect, useCallback } from 'react'
import { IconPlay, IconPause } from '@/components/icons/Icons'

interface AudioPlayerProps {
  src: string
  duration?: number
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

/** Duración desconocida: un guion es honesto, "0:00" hace creer que el audio está vacío. */
const SIN_DURACION = '–:––'

// Generate consistent pseudo-random waveform heights from src string
function generateBars(count: number, seed: string): number[] {
  let hash = 0
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0
  }
  const bars: number[] = []
  for (let i = 0; i < count; i++) {
    hash = ((hash << 5) - hash + i * 7) | 0
    const h = 0.2 + (Math.abs(hash % 80) / 100)
    bars.push(h)
  }
  return bars
}

const SPEEDS = [1, 1.5, 2]

export default function AudioPlayer({ src, duration: initialDuration }: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null)
  const [playing, setPlaying] = useState(false)
  const [currentTime, setCurrent] = useState(0)
  const [duration, setDuration] = useState(initialDuration ?? 0)
  const [speedIdx, setSpeedIdx] = useState(0)
  const resolviendo = useRef(false)
  const bars = generateBars(32, src)

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const onLoaded = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setDuration(audio.duration)
        return
      }
      // Las notas de voz de WhatsApp son OGG/Opus servidas en streaming: el
      // contenedor no declara la duración, así que el navegador reporta Infinity
      // hasta reproducir el archivo entero. Saltar al final lo obliga a calcularla;
      // apenas la sabe, se vuelve al inicio (abajo, en onDurationChange).
      if (resolviendo.current) return
      resolviendo.current = true
      try {
        audio.currentTime = 1e101
      } catch {
        resolviendo.current = false
      }
    }

    const onDurationChange = () => {
      if (!resolviendo.current) return
      if (!isFinite(audio.duration) || audio.duration <= 0) return
      setDuration(audio.duration)
      audio.currentTime = 0
      setCurrent(0)
      resolviendo.current = false
    }

    // Mientras se resuelve la duración, currentTime vale 1e101: mostrarlo sería basura.
    const onTime = () => { if (!resolviendo.current) setCurrent(audio.currentTime) }
    const onEnd = () => { setPlaying(false); setCurrent(0) }

    audio.addEventListener('loadedmetadata', onLoaded)
    audio.addEventListener('durationchange', onDurationChange)
    audio.addEventListener('timeupdate', onTime)
    audio.addEventListener('ended', onEnd)
    return () => {
      audio.removeEventListener('loadedmetadata', onLoaded)
      audio.removeEventListener('durationchange', onDurationChange)
      audio.removeEventListener('timeupdate', onTime)
      audio.removeEventListener('ended', onEnd)
    }
  }, [])

  const togglePlay = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    if (playing) {
      audio.pause()
    } else {
      audio.play().catch(() => { /* user gesture required */ })
    }
    setPlaying(!playing)
  }, [playing])

  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current
    if (!audio || !duration) return
    const rect = e.currentTarget.getBoundingClientRect()
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
    audio.currentTime = ratio * duration
    setCurrent(ratio * duration)
  }

  const cycleSpeed = () => {
    const next = (speedIdx + 1) % SPEEDS.length
    setSpeedIdx(next)
    if (audioRef.current) {
      audioRef.current.playbackRate = SPEEDS[next]
    }
  }

  const progress = duration > 0 ? currentTime / duration : 0
  const sonando = playing || currentTime > 0

  return (
    <div className="chat-audio-player">
      <audio ref={audioRef} src={src} preload="metadata" />

      <button
        className="chat-audio-player__btn"
        onClick={togglePlay}
        type="button"
      >
        {playing ? <IconPause size={16} /> : <IconPlay size={16} />}
      </button>

      <div className="chat-audio-player__waveform" onClick={seek}>
        {bars.map((h, i) => (
          <div
            key={i}
            className={`chat-audio-player__bar ${i / bars.length <= progress ? 'chat-audio-player__bar--active' : ''}`}
            style={{ height: `${h * 100}%` }}
          />
        ))}
      </div>

      {/* Igual que WhatsApp: en reposo, cuánto dura; sonando, cuánto va. Antes
          mostraba siempre `currentTime`, que en reposo es 0 — de ahí el "0:00". */}
      <span className="chat-audio-player__time">
        {sonando
          ? formatTime(currentTime)
          : duration > 0 ? formatTime(duration) : SIN_DURACION}
      </span>

      <button
        className="chat-audio-player__speed"
        onClick={cycleSpeed}
        type="button"
        title="Velocidad"
      >
        {SPEEDS[speedIdx]}x
      </button>
    </div>
  )
}
